import { Card } from '@mui/material';
import { BankOperationStatusChipField } from 'pages/Finance/BankOperations/components/BankOperationStatusChipField/BankOperationStatusChipField';
import { useUpdateBankOperationsComment } from 'pages/Finance/BankOperations/hooks/useUpdateBankOperationsComment';
import { queryClient } from 'providers/queryClient';
import { maxLength, RecordContextProvider, required, useNotify } from 'react-admin';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { CancelButton, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardTable } from 'shared/mui/Card/CardTableNew/CardTable';
import { Dialog, DialogProps } from 'shared/mui/Dialogs/Dialog/Dialog';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { TableRowWrapper } from 'shared/mui/Table/TableRowWrapperNew/TableRowWrapper';
import { FunctionField, TextField } from 'shared/react-admin/Fields';
import { TextInput } from 'shared/react-admin/Inputs';
import { BankOperation } from 'types/embedded/bankOperation';
import { getId } from 'utils';

type Props = {
  record: BankOperation | undefined;
} & DialogProps;

type FormValues = {
  comment: string;
};

export const BankOperationsEditCommentDialog: React.FC<Props> = ({ record, ...restProps }) => {
  const form = useForm<FormValues>();
  const notify = useNotify();
  const { mutate: updateBankOperationsComment } = useUpdateBankOperationsComment();

  const onClose = () => {
    form.reset();
    restProps.onClose();
  };

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    updateBankOperationsComment(
      {
        id: getId(record?.id),
        data: {
          description: values?.comment,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries();
          onClose();
        },
        onError: (error: any) => notify(error?.data?.errors[0]?.title, { type: 'error' }),
      },
    );
  };

  return (
    <Dialog {...restProps} width="640px">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card>
            <CardHeader
              appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
              title="Редактирование комментария"
              titleSize="smallBold"
            />
            <CardContent>
              <RecordContextProvider value={record}>
                <CardTable tableBodyProps={{ sx: { '& td': { width: '208px' } } }}>
                  <TableRowWrapper label="ID">
                    <TextField source="originId" />
                  </TableRowWrapper>
                  <TableRowWrapper label="Реферал">
                    <FunctionField
                      render={(value: BankOperation) =>
                        value?.referral
                          ? `${value.referral.refId}: ${value.referral.name}`
                          : undefined
                      }
                    />
                  </TableRowWrapper>
                  <TableRowWrapper label="Мерчант">
                    <TextField source="merchant.name" />
                  </TableRowWrapper>
                  <TableRowWrapper label="Провайдер">
                    <TextField source="provider.name" />
                  </TableRowWrapper>
                  <TableRowWrapper label="Статус">
                    <RecordContextProvider value={record?.status}>
                      <BankOperationStatusChipField source="description" />
                    </RecordContextProvider>
                  </TableRowWrapper>
                  <TableRowWrapper label="Комментарий">
                    <TextField
                      emptyText="—"
                      source="description"
                      sx={{ wordBreak: 'break-word' }}
                    />
                  </TableRowWrapper>
                </CardTable>
              </RecordContextProvider>
            </CardContent>
            <CardContent>
              <Box>
                <TextInput
                  fullWidth={true}
                  helperText={false}
                  label="Новый комментарий"
                  multiline={true}
                  resettable
                  size="small"
                  source="comment"
                  validate={[
                    required(),
                    maxLength(
                      1024,
                      'Это значение слишком длинное. Оно должно содержать 1024 символа или меньше.',
                    ),
                  ]}
                  variant="outlined"
                />
              </Box>
            </CardContent>
            <CardActionsButtons
              rightActionsSlot={[
                <CancelButton
                  key="back-button"
                  label="Отменить"
                  onClick={onClose}
                  variant="outlined"
                />,
                <SaveButton key="ok-button" label="Сохранить" />,
              ]}
            />
          </Card>
        </form>
      </FormProvider>
    </Dialog>
  );
};
